import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import {
  Package, Plus, Search, Edit2, UserPlus, RotateCcw, Trash2, Building2, Download
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { value: "laptop", label: "Laptop" },
  { value: "mobile", label: "Mobile" },
  { value: "desktop", label: "Desktop" },
  { value: "monitor", label: "Monitor" },
  { value: "furniture", label: "Furniture" },
  { value: "vehicle", label: "Vehicle" },
  { value: "other", label: "Other" },
];

const CONDITIONS = [
  { value: "new", label: "New" },
  { value: "good", label: "Good" },
  { value: "fair", label: "Fair" },
  { value: "poor", label: "Poor" },
];

const STATUS_COLORS = {
  available: "bg-green-100 text-green-700",
  assigned: "bg-blue-100 text-blue-700",
  maintenance: "bg-yellow-100 text-yellow-700",
  retired: "bg-slate-100 text-slate-500",
};

const Assets = () => {
  const { currentBusiness } = useAuth();
  const [assets, setAssets] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  
  // Dialogs
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [showReturnDialog, setShowReturnDialog] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({
    name: "", category: "laptop", description: "", serial_number: "",
    purchase_date: "", purchase_cost: "", vendor: "", warranty_expiry: "",
    condition: "new", location: "", notes: "",
  });
  const [assignData, setAssignData] = useState({ employee_id: "", notes: "" });
  const [returnData, setReturnData] = useState({ condition: "good", notes: "" });

  const fetchAssets = useCallback(async () => {
    if (!currentBusiness?.id) return;
    setLoading(true);
    try {
      const res = await api.get(`/assets?business_id=${currentBusiness.id}`);
      setAssets(res.data || []);
    } catch (err) {
      toast.error("Failed to load assets");
    } finally {
      setLoading(false);
    }
  }, [currentBusiness?.id]);

  const fetchEmployees = useCallback(async () => {
    if (!currentBusiness?.id) return;
    try {
      const res = await api.get(`/employees?business_id=${currentBusiness.id}`);
      setEmployees((res.data || []).filter(e => e.is_active));
    } catch (err) {
      console.error("Failed to load employees");
    }
  }, [currentBusiness?.id]);

  useEffect(() => {
    fetchAssets();
    fetchEmployees();
  }, [fetchAssets, fetchEmployees]);

  const resetForm = () => {
    setFormData({
      name: "", category: "laptop", description: "", serial_number: "",
      purchase_date: "", purchase_cost: "", vendor: "", warranty_expiry: "",
      condition: "new", location: "", notes: "",
    });
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!formData.name) {
      toast.error("Asset name is required");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/assets", { ...formData, business_id: currentBusiness.id });
      toast.success("Asset created");
      setShowAddDialog(false);
      resetForm();
      fetchAssets();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create asset");
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.put(`/assets/${selectedAsset.id}`, formData);
      toast.success("Asset updated");
      setShowAddDialog(false);
      setSelectedAsset(null);
      resetForm();
      fetchAssets();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update asset");
    } finally {
      setSubmitting(false);
    }
  };

  const handleAssign = async (e) => {
    e.preventDefault();
    if (!assignData.employee_id) {
      toast.error("Select an employee");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/assets/${selectedAsset.id}/assign`, assignData);
      toast.success("Asset assigned");
      setShowAssignDialog(false);
      setAssignData({ employee_id: "", notes: "" });
      setSelectedAsset(null);
      fetchAssets();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to assign asset");
    } finally {
      setSubmitting(false);
    }
  };

  const handleReturn = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post(`/assets/${selectedAsset.id}/return`, returnData);
      toast.success("Asset returned");
      setShowReturnDialog(false);
      setReturnData({ condition: "good", notes: "" });
      setSelectedAsset(null);
      fetchAssets();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to return asset");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (asset) => {
    if (!window.confirm(`Retire asset "${asset.name}"?`)) return;
    try {
      await api.delete(`/assets/${asset.id}`);
      toast.success("Asset retired");
      fetchAssets();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to retire asset");
    }
  };

  const handleExport = async (format = 'excel') => {
    try {
      const token = localStorage.getItem("token");
      const url = `${BACKEND_URL}/api/assets/export?business_id=${currentBusiness.id}&format=${format}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `assets_${new Date().toISOString().split("T")[0]}.${format === 'excel' ? 'xlsx' : format}`;
      a.click();
      window.URL.revokeObjectURL(blobUrl);
      toast.success("Assets exported successfully");
    } catch (err) {
      toast.error("Failed to export assets");
    }
  };

  const openEditDialog = (asset) => {
    setSelectedAsset(asset);
    setFormData({
      name: asset.name || "",
      category: asset.category || "laptop",
      description: asset.description || "",
      serial_number: asset.serial_number || "",
      purchase_date: asset.purchase_date || "",
      purchase_cost: asset.purchase_cost || "",
      vendor: asset.vendor || "",
      warranty_expiry: asset.warranty_expiry || "",
      condition: asset.condition || "good",
      location: asset.location || "",
      notes: asset.notes || "",
    });
    setShowAddDialog(true);
  };

  const filteredAssets = assets.filter(a => {
    const matchesSearch = !search || 
      a.name?.toLowerCase().includes(search.toLowerCase()) ||
      a.asset_code?.toLowerCase().includes(search.toLowerCase()) ||
      a.serial_number?.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === "all" || a.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getEmployeeName = (empId) => {
    const emp = employees.find(e => e.id === empId);
    return emp ? `${emp.first_name} ${emp.last_name}` : "-";
  };

  if (!currentBusiness) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
        <Building2 className="h-16 w-16 text-slate-300 mb-4" />
        <h2 className="text-2xl font-semibold text-slate-800 font-heading mb-2">No Business Selected</h2>
        <p className="text-slate-500">Select a business to manage assets</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="assets-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 font-heading">Asset Management</h1>
          <p className="text-slate-500">Track and manage company assets</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="rounded-full" onClick={() => handleExport('excel')}
            data-testid="export-assets-btn">
            <Download className="h-4 w-4 mr-2" /> Export
          </Button>
          <Button className="btn-primary" onClick={() => { resetForm(); setSelectedAsset(null); setShowAddDialog(true); }}
            data-testid="add-asset-btn">
            <Plus className="h-4 w-4 mr-2" /> Add Asset
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card className="card-soft">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-slate-800">{assets.length}</div>
            <div className="text-sm text-slate-500">Total Assets</div>
          </CardContent>
        </Card>
        <Card className="card-soft">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-green-600">{assets.filter(a => a.status === "available").length}</div>
            <div className="text-sm text-slate-500">Available</div>
          </CardContent>
        </Card>
        <Card className="card-soft">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-blue-600">{assets.filter(a => a.status === "assigned").length}</div>
            <div className="text-sm text-slate-500">Assigned</div>
          </CardContent>
        </Card>
        <Card className="card-soft">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-slate-500">{assets.filter(a => a.status === "retired").length}</div>
            <div className="text-sm text-slate-500">Retired</div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="card-soft">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input placeholder="Search assets..." value={search} onChange={e => setSearch(e.target.value)}
                className="pl-10 input-field" data-testid="search-assets" />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[150px]" data-testid="filter-status">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="available">Available</SelectItem>
                <SelectItem value="assigned">Assigned</SelectItem>
                <SelectItem value="maintenance">Maintenance</SelectItem>
                <SelectItem value="retired">Retired</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Assets Table */}
      <Card className="card-soft">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center items-center h-48">
              <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div>
            </div>
          ) : filteredAssets.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-48 text-slate-500">
              <Package className="h-12 w-12 mb-3 text-slate-300" />
              <p>No assets found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50/50">
                    <TableHead className="font-semibold">Asset Code</TableHead>
                    <TableHead className="font-semibold">Name</TableHead>
                    <TableHead className="font-semibold">Category</TableHead>
                    <TableHead className="font-semibold">Serial No.</TableHead>
                    <TableHead className="font-semibold">Assigned To</TableHead>
                    <TableHead className="font-semibold">Condition</TableHead>
                    <TableHead className="font-semibold">Status</TableHead>
                    <TableHead className="font-semibold text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredAssets.map(asset => (
                    <TableRow key={asset.id} className="hover:bg-slate-50/50" data-testid={`asset-row-${asset.id}`}>
                      <TableCell className="font-mono text-sm">{asset.asset_code}</TableCell>
                      <TableCell className="font-medium">{asset.name}</TableCell>
                      <TableCell className="capitalize">{asset.category}</TableCell>
                      <TableCell className="text-slate-500">{asset.serial_number || "-"}</TableCell>
                      <TableCell>{asset.assigned_to ? getEmployeeName(asset.assigned_to) : "-"}</TableCell>
                      <TableCell className="capitalize">{asset.condition}</TableCell>
                      <TableCell>
                        <Badge className={`${STATUS_COLORS[asset.status] || "bg-slate-100"} capitalize`}>
                          {asset.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="icon" onClick={() => openEditDialog(asset)}
                            data-testid={`edit-asset-${asset.id}`}>
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          {asset.status === "available" && (
                            <Button variant="ghost" size="icon" onClick={() => { setSelectedAsset(asset); setShowAssignDialog(true); }}
                              data-testid={`assign-asset-${asset.id}`}>
                              <UserPlus className="h-4 w-4 text-blue-600" />
                            </Button>
                          )}
                          {asset.status === "assigned" && (
                            <Button variant="ghost" size="icon" onClick={() => { setSelectedAsset(asset); setShowReturnDialog(true); }}
                              data-testid={`return-asset-${asset.id}`}>
                              <RotateCcw className="h-4 w-4 text-green-600" />
                            </Button>
                          )}
                          {asset.status !== "assigned" && asset.status !== "retired" && (
                            <Button variant="ghost" size="icon" onClick={() => handleDelete(asset)}
                              data-testid={`delete-asset-${asset.id}`}>
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedAsset ? "Edit Asset" : "Add New Asset"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={selectedAsset ? handleUpdate : handleCreate} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Asset Name *</Label>
                <Input value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})}
                  placeholder="e.g. MacBook Pro 14" data-testid="asset-name-input" />
              </div>
              <div className="space-y-2">
                <Label>Category</Label>
                <Select value={formData.category} onValueChange={v => setFormData({...formData, category: v})}>
                  <SelectTrigger data-testid="asset-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Serial Number</Label>
                <Input value={formData.serial_number} onChange={e => setFormData({...formData, serial_number: e.target.value})}
                  placeholder="SN123456" data-testid="asset-serial-input" />
              </div>
              <div className="space-y-2">
                <Label>Vendor</Label>
                <Input value={formData.vendor} onChange={e => setFormData({...formData, vendor: e.target.value})}
                  placeholder="Apple Inc." data-testid="asset-vendor-input" />
              </div>
              <div className="space-y-2">
                <Label>Purchase Date</Label>
                <Input type="date" value={formData.purchase_date} onChange={e => setFormData({...formData, purchase_date: e.target.value})}
                  data-testid="asset-purchase-date-input" />
              </div>
              <div className="space-y-2">
                <Label>Purchase Cost (₹)</Label>
                <Input type="number" value={formData.purchase_cost} onChange={e => setFormData({...formData, purchase_cost: e.target.value})}
                  placeholder="50000" data-testid="asset-cost-input" />
              </div>
              <div className="space-y-2">
                <Label>Warranty Expiry</Label>
                <Input type="date" value={formData.warranty_expiry} onChange={e => setFormData({...formData, warranty_expiry: e.target.value})}
                  data-testid="asset-warranty-input" />
              </div>
              <div className="space-y-2">
                <Label>Condition</Label>
                <Select value={formData.condition} onValueChange={v => setFormData({...formData, condition: v})}>
                  <SelectTrigger data-testid="asset-condition-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CONDITIONS.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Location</Label>
                <Input value={formData.location} onChange={e => setFormData({...formData, location: e.target.value})}
                  placeholder="Office - Floor 2" data-testid="asset-location-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})}
                placeholder="Asset details..." rows={2} data-testid="asset-desc-input" />
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea value={formData.notes} onChange={e => setFormData({...formData, notes: e.target.value})}
                placeholder="Additional notes..." rows={2} data-testid="asset-notes-input" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
              <Button type="submit" className="btn-primary" disabled={submitting} data-testid="save-asset-btn">
                {submitting ? "Saving..." : (selectedAsset ? "Update Asset" : "Create Asset")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Assign Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Asset</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAssign} className="space-y-4">
            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="text-sm text-slate-600">Asset: <strong>{selectedAsset?.name}</strong></p>
              <p className="text-sm text-slate-500">Code: {selectedAsset?.asset_code}</p>
            </div>
            <div className="space-y-2">
              <Label>Assign to Employee *</Label>
              <Select value={assignData.employee_id} onValueChange={v => setAssignData({...assignData, employee_id: v})}>
                <SelectTrigger data-testid="assign-employee-select"><SelectValue placeholder="Select employee" /></SelectTrigger>
                <SelectContent>
                  {employees.map(e => (
                    <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name} ({e.employee_code})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea value={assignData.notes} onChange={e => setAssignData({...assignData, notes: e.target.value})}
                placeholder="Assignment notes..." rows={2} data-testid="assign-notes-input" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAssignDialog(false)}>Cancel</Button>
              <Button type="submit" className="btn-primary" disabled={submitting} data-testid="confirm-assign-btn">
                {submitting ? "Assigning..." : "Assign Asset"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Return Dialog */}
      <Dialog open={showReturnDialog} onOpenChange={setShowReturnDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Return Asset</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleReturn} className="space-y-4">
            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="text-sm text-slate-600">Asset: <strong>{selectedAsset?.name}</strong></p>
              <p className="text-sm text-slate-500">Currently with: {selectedAsset?.assigned_to ? getEmployeeName(selectedAsset.assigned_to) : "-"}</p>
            </div>
            <div className="space-y-2">
              <Label>Condition on Return</Label>
              <Select value={returnData.condition} onValueChange={v => setReturnData({...returnData, condition: v})}>
                <SelectTrigger data-testid="return-condition-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CONDITIONS.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea value={returnData.notes} onChange={e => setReturnData({...returnData, notes: e.target.value})}
                placeholder="Return notes, any damage..." rows={2} data-testid="return-notes-input" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowReturnDialog(false)}>Cancel</Button>
              <Button type="submit" className="btn-primary" disabled={submitting} data-testid="confirm-return-btn">
                {submitting ? "Processing..." : "Confirm Return"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Assets;
